/**
 * ==================== SWIPE TO ACTION COMPONENT ====================
 * Componente de deslizar para executar ação.
 * Previne cliques acidentais exigindo que o usuário arraste até o fim.
 * 
 * SAFE PATCH: Apenas alteração visual de UX, sem mudanças no backend.
 * ===================================================================
 */
import React, { useState, useRef, useCallback } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';

export default function SwipeToAction({ 
  onSwipeComplete, 
  label = "Deslize para confirmar",
  icon: Icon = ChevronRight,
  disabled = false,
  loading = false,
  className = "",
  trackColor = "bg-blue-100",
  thumbColor = "bg-blue-600",
  completeColor = "bg-green-500"
}) {
  const [position, setPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const trackRef = useRef(null);
  const startXRef = useRef(0);
  
  // Configurações
  const THUMB_WIDTH = 56; // px
  const COMPLETE_THRESHOLD = 0.85; // 85% para completar
  
  const getTrackWidth = useCallback(() => {
    if (trackRef.current) {
      return trackRef.current.offsetWidth - THUMB_WIDTH;
    }
    return 200;
  }, []);
  
  const handleStart = useCallback((clientX) => {
    if (disabled || loading || isComplete) return;
    setIsDragging(true);
    startXRef.current = clientX - position;
  }, [disabled, loading, isComplete, position]);
  
  const handleMove = useCallback((clientX) => {
    if (!isDragging || disabled || loading || isComplete) return;
    
    const trackWidth = getTrackWidth();
    const newPosition = Math.max(0, Math.min(clientX - startXRef.current, trackWidth));
    setPosition(newPosition);
    
    // Verificar se completou
    const progress = newPosition / trackWidth;
    if (progress >= COMPLETE_THRESHOLD) {
      setIsComplete(true);
      setIsDragging(false);
      setPosition(trackWidth);
      
      // Executar callback
      if (onSwipeComplete) {
        onSwipeComplete();
      }
    }
  }, [isDragging, disabled, loading, isComplete, getTrackWidth, onSwipeComplete]);
  
  const handleEnd = useCallback(() => {
    if (!isDragging || isComplete) return;
    setIsDragging(false);
    
    // Se não completou, voltar ao início
    const trackWidth = getTrackWidth();
    const progress = position / trackWidth;
    
    if (progress < COMPLETE_THRESHOLD) {
      setPosition(0);
    }
  }, [isDragging, isComplete, position, getTrackWidth]);
  
  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };
  
  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };
  
  const handleMouseUp = () => {
    handleEnd();
  };
  
  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    handleEnd();
  };
  
  // Calcular progresso para efeitos visuais
  const trackWidth = trackRef.current?.offsetWidth - THUMB_WIDTH || 200;
  const progress = Math.min(position / trackWidth, 1);
  
  // Reset quando loading terminar
  React.useEffect(() => {
    if (!loading && isComplete) {
      // Resetar após ação completada
      const timer = setTimeout(() => {
        setIsComplete(false);
        setPosition(0);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, isComplete]);
  
  return (
    <div 
      ref={trackRef}
      className={`
        relative h-14 rounded-full overflow-hidden select-none
        ${isComplete ? completeColor : trackColor}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab'}
        ${isDragging ? 'cursor-grabbing' : ''}
        ${className}
        transition-colors duration-300
      `}
      onMouseMove={isDragging ? handleMouseMove : undefined}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="swipe-track"
    >
      {/* Fundo com gradiente de progresso */}
      <div 
        className={`absolute inset-0 ${isComplete ? completeColor : 'bg-blue-200'} transition-all duration-100`}
        style={{ width: `${position + THUMB_WIDTH}px` }}
      />
      
      {/* Texto central */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span 
          className={`
            text-sm font-medium transition-opacity duration-200
            ${isComplete ? 'text-white' : 'text-blue-800'}
            ${progress > 0.3 ? 'opacity-0' : 'opacity-100'}
          `}
        >
          {loading ? 'Processando...' : isComplete ? 'Iniciado!' : label}
        </span>
      </div>
      
      {/* Setas de indicação (aparecem quando não está arrastando) */}
      {!isDragging && !isComplete && !loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-1 ml-16 animate-pulse">
            <ChevronRight className="w-5 h-5 text-blue-400" />
            <ChevronRight className="w-5 h-5 text-blue-300 -ml-3" />
            <ChevronRight className="w-5 h-5 text-blue-200 -ml-3" />
          </div>
        </div>
      )}
      
      {/* Thumb (botão deslizante) */}
      <div
        className={`
          absolute top-1 bottom-1 left-1 w-12 rounded-full
          flex items-center justify-center
          ${isComplete ? 'bg-white' : thumbColor}
          ${disabled ? '' : 'shadow-lg'}
          transition-colors duration-200
          ${isDragging ? 'scale-105' : 'scale-100'}
        `}
        style={{ 
          transform: `translateX(${position}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="swipe-thumb"
      >
        {loading ? (
          <Loader2 className={`w-5 h-5 animate-spin ${isComplete ? 'text-green-500' : 'text-white'}`} />
        ) : (
          <Icon className={`w-5 h-5 ${isComplete ? 'text-green-500' : 'text-white'}`} />
        )}
      </div>
    </div>
  );
}
